import {
  Component, Input, Output, EventEmitter, OnInit, OnDestroy,
  ChangeDetectionStrategy, ChangeDetectorRef, forwardRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule, ReactiveFormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';

export interface MentionUser {
  id: number;
  name: string;
  email: string;
}

/**
 * MentionInputComponent
 *
 * A textarea that detects @mentions, shows a user typeahead dropdown,
 * and emits the list of mentioned users alongside the text value.
 *
 * Implements ControlValueAccessor so it works with both template-driven
 * and reactive forms via [formControlName] / [(ngModel)].
 *
 * Usage:
 *   <calsoft-mention-input
 *     [userList]="users"
 *     [placeholder]="'Add a comment...'"
 *     (mentionsChanged)="onMentions($event)"
 *     formControlName="notes">
 *   </calsoft-mention-input>
 */
@Component({
  selector: 'calsoft-mention-input',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MentionInputComponent),
    multi: true
  }],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatListModule,
    MatIconModule
  ],
  templateUrl: './mention-input.component.html',
  styleUrls: ['./mention-input.component.scss']
})
export class MentionInputComponent implements OnInit, OnDestroy, ControlValueAccessor {

  @Input() userList: MentionUser[] = [];
  @Input() placeholder = 'Type here... use @name to mention teammates';
  @Input() rows = 3;

  @Output() mentionsChanged = new EventEmitter<MentionUser[]>();

  text = '';
  showDropdown = false;
  filteredUsers: MentionUser[] = [];
  mentionedUsers: MentionUser[] = [];

  /** Cursor position when @ was typed (used to insert mention) */
  private mentionStart = -1;
  private textSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  private onChange: (v: string) => void = () => {};
  onTouched: () => void = () => {};

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.textSubject.pipe(
      debounceTime(150),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(query => {
      this.filterUsers(query);
    });
  }

  onInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.text = target.value;
    this.onChange(this.text);

    // Detect if cursor is after '@'
    const cursor = target.selectionStart ?? 0;
    const textBefore = this.text.slice(0, cursor);
    const lastAtIndex = textBefore.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const query = textBefore.slice(lastAtIndex + 1);
      // Only show dropdown if query has no space (single token)
      if (!query.includes(' ') && query.length <= 30) {
        this.mentionStart = lastAtIndex;
        this.textSubject.next(query);
        this.showDropdown = true;
        return;
      }
    }

    this.showDropdown = false;
    this.mentionStart = -1;
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.showDropdown = false;
    }
  }

  selectUser(user: MentionUser, textarea: HTMLTextAreaElement): void {
    if (this.mentionStart === -1) return;

    const cursor = textarea.selectionStart ?? 0;
    const before = this.text.slice(0, this.mentionStart);
    const after  = this.text.slice(cursor);
    const replacement = `@${user.name} `;

    this.text = before + replacement + after;
    this.onChange(this.text);

    // Add to mentioned list (dedup)
    if (!this.mentionedUsers.find(u => u.id === user.id)) {
      this.mentionedUsers = [...this.mentionedUsers, user];
      this.mentionsChanged.emit(this.mentionedUsers);
    }

    this.showDropdown = false;
    this.mentionStart = -1;

    // Restore focus & move cursor after mention
    setTimeout(() => {
      textarea.focus();
      const pos = before.length + replacement.length;
      textarea.setSelectionRange(pos, pos);
    });
    this.cdr.markForCheck();
  }

  removeMention(user: MentionUser): void {
    this.mentionedUsers = this.mentionedUsers.filter(u => u.id !== user.id);
    this.mentionsChanged.emit(this.mentionedUsers);
    this.cdr.markForCheck();
  }

  private filterUsers(query: string): void {
    const q = query.toLowerCase();
    this.filteredUsers = q
      ? this.userList.filter(u => u.name.toLowerCase().includes(q)).slice(0, 6)
      : this.userList.slice(0, 6);
    this.cdr.markForCheck();
  }

  // ControlValueAccessor
  writeValue(val: string): void {
    this.text = val ?? '';
    this.cdr.markForCheck();
  }
  registerOnChange(fn: (v: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
